import {
	CreatePropositionDocumentDto,
	GenerateDocumentDto,
	GenerateDocumentResponse,
	GenerateMultipleDocumentsDto,
	GenerateMultipleDocumentsResponse,
	Proposition,
	PropositionDocument,
	SendDocumentForSignatureRequest,
	SendDocumentForSignatureResponse,
	UpdatePropositionDto,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import { propositionsQueryKeys } from "../query-keys/propositions.query-keys";
import { tasksQueryKeys } from "../query-keys/tasks.query-keys";
import { contractsKeys } from "../queries/contracts.queries";

export interface LiftReservesRequest {
	propositionId: number;
	reservesToLift: string[];
}

export interface GeneratePropositionsRequest {
	constructionSiteSubmissionId: number;
	brokerSocietyId: number;
}

const updateProposition = async ({
	id,
	...data
}: UpdatePropositionDto & { id: number }): Promise<Proposition> => {
	const { data: response } = await privateAxios.put<Proposition>(
		`/propositions/${id}`,
		data,
	);
	return response;
};

const liftReserves = async ({
	propositionId,
	reservesToLift,
}: LiftReservesRequest): Promise<Proposition> => {
	const { data } = await privateAxios.post<Proposition>(
		`/propositions/${propositionId}/lift-reserves`,
		{ reservesToLift },
	);
	return data;
};

const uploadPropositionDocument = async (
	data: CreatePropositionDocumentDto,
): Promise<PropositionDocument> => {
	const { propositionId, ...requestData } = data;
	const { data: response } = await privateAxios.post<PropositionDocument>(
		`/propositions/${propositionId}/documents`,
		requestData,
	);
	return response;
};

const generatePropositionsFromSubmission = async (
	data: GeneratePropositionsRequest,
): Promise<Proposition[]> => {
	const { data: response } = await privateAxios.post<Proposition[]>(
		"/propositions/generate",
		data,
	);
	return response;
};

export const useUpdateProposition = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: updateProposition,
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.all,
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.counts(),
				refetchType: "active",
			});
			if (data.constructionSiteId) {
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.byConstructionSite(
						data.constructionSiteId,
					),
					refetchType: "active",
				});
			}
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.byBroker(data.brokerSocietyId),
				refetchType: "active",
			});
			queryClient.setQueryData(propositionsQueryKeys.detail(data.id), data);
			toast.success("Proposition mise à jour avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la mise à jour de la proposition", {
				description: errorMessage,
			});
		},
	});
};

export const useLiftReserves = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: liftReserves,
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: propositionsQueryKeys.all });
			queryClient.setQueryData(propositionsQueryKeys.detail(data.id), data);
			toast.success("Réserves levées avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la levée des réserves", {
				description: errorMessage,
			});
		},
	});
};

export const useUploadPropositionDocument = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: uploadPropositionDocument,
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.documents(data.propositionId),
			});
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.detail(data.propositionId),
			});

			const contractDocumentsKey = [
				...contractsKeys.detail(data.propositionId),
				"documents",
			];

			queryClient.invalidateQueries({
				queryKey: contractDocumentsKey,
			});
			queryClient.invalidateQueries({
				queryKey: contractsKeys.detail(data.propositionId),
			});

			queryClient.invalidateQueries({
				predicate: (query) => {
					const matches =
						query.queryKey[0] === "contracts" &&
						query.queryKey[2] === data.propositionId;

					return matches;
				},
			});

			queryClient.invalidateQueries({
				queryKey: ["contracts"],
			});

			toast.success("Document ajouté avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'ajout du document", {
				description: errorMessage,
			});
		},
	});
};

export const useGeneratePropositionsFromSubmission = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: generatePropositionsFromSubmission,
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: propositionsQueryKeys.all });
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.byBroker(variables.brokerSocietyId),
			});
			toast.success(`${data.length} proposition(s) générée(s) avec succès`);
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la génération des propositions", {
				description: errorMessage,
			});
		},
	});
};

const generateDocument = async (
	propositionId: number,
	data: GenerateDocumentDto,
): Promise<GenerateDocumentResponse> => {
	const { data: response } = await privateAxios.post<GenerateDocumentResponse>(
		`/propositions/${propositionId}/generate-document`,
		data,
	);
	return response;
};

const generateMultipleDocuments = async (
	propositionId: number,
	data: GenerateMultipleDocumentsDto,
): Promise<GenerateMultipleDocumentsResponse> => {
	const { data: response } =
		await privateAxios.post<GenerateMultipleDocumentsResponse>(
			`/propositions/${propositionId}/generate-multiple-documents`,
			data,
		);
	return response;
};

export const useGenerateDocument = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: GenerateDocumentDto) =>
			generateDocument(propositionId, data),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.detail(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.documents(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.tasks(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: tasksQueryKeys.activeTasksList(),
					refetchType: "active",
				}),
			]);
		},
	});
};

export const useGenerateMultipleDocuments = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: GenerateMultipleDocumentsDto) =>
			generateMultipleDocuments(propositionId, data),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.detail(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.documents(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.tasks(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: tasksQueryKeys.activeTasksList(),
					refetchType: "active",
				}),
			]);
		},
	});
};

export const toggleSkipProposition = async (params: {
	id: number;
	skipReason?: string;
}): Promise<Proposition> => {
	const { data } = await privateAxios.put<Proposition>(
		`/propositions/${params.id}/skip`,
		{ skipReason: params.skipReason },
	);
	return data;
};

export const useToggleSkipProposition = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: toggleSkipProposition,
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.all,
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.counts(),
				refetchType: "active",
			});
			queryClient.setQueryData(propositionsQueryKeys.detail(data.id), data);

			if (variables.skipReason) {
				toast.success("La proposition a été passée sans suite");
			} else {
				toast.success("La proposition a été remise en cours");
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error(
				"Erreur lors de la modification du statut de la proposition",
				{
					description: errorMessage,
				},
			);
		},
	});
};

const sendDocumentForSignature = async (
	data: SendDocumentForSignatureRequest,
): Promise<SendDocumentForSignatureResponse> => {
	const { data: response } =
		await privateAxios.post<SendDocumentForSignatureResponse>(
			"/propositions/send-document",
			data,
		);
	return response;
};

export const useSendDocumentForSignature = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: sendDocumentForSignature,
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.detail(variables.propositionId),
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.tasks(variables.propositionId),
				refetchType: "active",
			});
		},
	});
};

const updatePropositionDuneComment = async (
	propositionId: number,
	duneComment: string | null,
): Promise<void> => {
	await privateAxios.patch(`/propositions/${propositionId}/dune-comment`, {
		duneComment,
	});
};

export const useUpdatePropositionDuneComment = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (duneComment: string | null) =>
			updatePropositionDuneComment(propositionId, duneComment),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.detail(propositionId),
				refetchType: "active",
			});
		},
	});
};

export const deletePropositionDocument = async (
	propositionId: number,
	documentId: string,
): Promise<void> => {
	await privateAxios.delete(
		`/propositions/${propositionId}/documents/${documentId}`,
	);
};

export const useDeletePropositionDocumentMutation = (
	propositionId: number,
	options?: {
		onMutate?: (documentId: string) => void;
		onSettled?: () => void;
	},
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (documentId: string) =>
			deletePropositionDocument(propositionId, documentId),
		onMutate: options?.onMutate,
		onSuccess: () => {
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "propositions" &&
					query.queryKey[1] === "documents" &&
					query.queryKey[2] === propositionId,
			});
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.detail(propositionId),
			});

			queryClient.invalidateQueries({
				queryKey: [...contractsKeys.detail(propositionId), "documents"],
			});
			queryClient.invalidateQueries({
				queryKey: contractsKeys.detail(propositionId),
			});
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "contracts" &&
					query.queryKey[2] === propositionId,
			});

			toast.success("Document supprimé avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de la suppression du document");
		},
		onSettled: options?.onSettled,
	});
};
